import Box from '@mui/material/Box';
import BugReportIcon from '@mui/icons-material/BugReport';
import SecurityIcon from '@mui/icons-material/Security';
import { getSecurityNavItemPath } from 'src/mindtime/config/securityNav';
import type { WazuhMalwareDetectionItem } from 'src/types/mindtime/wazuhMalware';
import type { Column } from 'src/types/table';
import {
  EDR_COLORS,
  EdrDateTime,
  EdrDetailEndpointSidebar,
  EdrDetailKeyValueRows,
  EdrDetailSectionCard,
  EdrExpandableText,
  EdrSeverityPill,
  EdrTruncatedText,
  edrMonoStyle,
  edrMutedStyle,
  formatEdrDateTime,
  formatEndpointLabel,
  type EdrEntityDetailPageProps,
  type EdrSeverityLevel,
} from '../shared/edrPrimitives';

import { toEdrSeverityLevel, EDR_SEVERITY_FILTER_OPTIONS } from '../shared/edrFormat';

export const malwareSeverityToLevel = toEdrSeverityLevel;
export const MALWARE_SEVERITY_FILTER_OPTIONS = EDR_SEVERITY_FILTER_OPTIONS;

const normalizeKey = (value: string | undefined | null): string =>
  (value ?? '').toString().trim().toLowerCase();

export const toMalwareTitle = (value: string | undefined | null): string => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};


/** Id for GET /malware/detections/{id} — prefers `id`, then `_id`. */
export const getMalwareDetectionId = (
  item: Pick<WazuhMalwareDetectionItem, 'id' | '_id'> | null | undefined
): string => String(item?.id ?? item?._id ?? '').trim();

/** Short page/header title for long rule-style threat names. */
export const formatMalwareThreatTitle = (threatName?: string | null): string => {
  const name = threatName?.trim() || 'Malware Detection';
  return name.length > 96 ? `${name.slice(0, 93)}…` : name;
};


type MalwareListColumnScope = 'global' | 'endpoint';

export const getMalwareDetectionListColumns = (scope: MalwareListColumnScope): Column[] => {
  const isEndpoint = scope === 'endpoint';

  return [
    {
      id: 'threatName',
      label: 'Threat Name',
      width: isEndpoint ? '40%' : '35%',
      render: (row) => (
        <EdrTruncatedText
          value={(row as unknown as WazuhMalwareDetectionItem).threatName || '—'}
          style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}
          maxWidth={470}
        />
      ),
    },
    {
      id: 'malwareType',
      label: 'Malware Type',
      width: isEndpoint ? '18%' : '13%',
      render: (row) => (
        <EdrTruncatedText
          value={(row as unknown as WazuhMalwareDetectionItem).malwareType || '—'}
          style={edrMutedStyle}
          maxWidth="100%"
        />
      ),
    },
    ...(isEndpoint
      ? []
      : [
          {
            id: 'endpoint',
            label: 'Endpoint',
            width: '13%',
            render: (row) => (
              <EdrTruncatedText
                value={(row as unknown as WazuhMalwareDetectionItem).endpoint || '—'}
                style={edrMonoStyle}
                maxWidth="100%"
              />
            ),
          },
          {
            id: 'hostName',
            label: 'Endpoint Label',
            width: '12%',
            render: (row) => (
              <EdrTruncatedText
                value={formatEndpointLabel((row as unknown as WazuhMalwareDetectionItem).hostName)}
                style={edrMutedStyle}
                maxWidth="100%"
              />
            ),
          },
          {
            id: 'organization',
            label: 'Organization',
            width: '15%',
            render: (row) => (
              <EdrTruncatedText
                value={(row as unknown as WazuhMalwareDetectionItem).organization || '—'}
                style={edrMutedStyle}
                maxWidth="100%"
              />
            ),
          },
        ]),
    {
      id: 'severity',
      label: 'Severity',
      width: isEndpoint ? '18%' : '12%',
      render: (row) => {
        const item = row as unknown as WazuhMalwareDetectionItem;
        return (
          <EdrSeverityPill
            level={malwareSeverityToLevel(item.severity)}
            label={toMalwareTitle(item.severity) || '—'}
          />
        );
      },
    },
    {
      id: 'timestamp',
      label: 'Detection Time',
      width: isEndpoint ? '24%' : '15%',
      render: (row) => (
        <EdrDateTime value={(row as unknown as WazuhMalwareDetectionItem).timestamp} />
      ),
    },
  ];
};

export const buildMalwareDetailProps = (
  item: WazuhMalwareDetectionItem
): Omit<EdrEntityDetailPageProps, 'loading' | 'notFoundMessage'> => {
  const severityLevel = malwareSeverityToLevel(item.severity);
  const detectionId = getMalwareDetectionId(item) || item.wazuhAlertId || '—';
  const familyOrType = item.malwareFamily?.trim() || item.malwareType || '—';

  return {
    backLink: {
      label: 'Back to Malware Detection',
      to: getSecurityNavItemPath('malware-detection') ?? '',
    },
    title: formatMalwareThreatTitle(item.threatName),
    badges: (
      <EdrSeverityPill level={severityLevel} label={toMalwareTitle(item.severity) || '—'} />
    ),
    metaLine: (
      <>
        {detectionId}
        {' · '}
        {formatEdrDateTime(item.timestamp)}
        {' · '}
        <strong style={{ color: EDR_COLORS.textPrimary }}>{item.endpoint || '—'}</strong>
      </>
    ),
    main: (
      <>
        <EdrDetailSectionCard
          icon={<BugReportIcon style={{ fontSize: 16 }} />}
          title="Threat Overview"
        >
          <EdrDetailKeyValueRows
            rows={[
              {
                label: 'Threat Name',
                value: item.threatName ? (
                  <EdrExpandableText text={item.threatName} lines={2} />
                ) : (
                  '—'
                ),
              },
              { label: 'Family / Type', value: familyOrType },
              { label: 'Severity', value: toMalwareTitle(item.severity) || '—' },
              { label: 'Detection Time', value: <EdrDateTime value={item.timestamp} /> },
            ]}
          />
        </EdrDetailSectionCard>

        <EdrDetailSectionCard icon={<SecurityIcon style={{ fontSize: 16 }} />} title="Rule Details">
          {item.rule?.description ? (
            <Box mb={1.5}>
              <EdrExpandableText text={item.rule.description} lines={3} />
            </Box>
          ) : null}
          <EdrDetailKeyValueRows
            rows={[
              {
                label: 'Rule ID',
                value: item.rule?.id != null ? String(item.rule.id) : '—',
                mono: true,
              },
              {
                label: 'Level',
                value: typeof item.rule?.level === 'number' ? String(item.rule.level) : '—',
              },
              {
                label: 'Groups',
                value: item.rule?.groups?.length ? item.rule.groups.join(', ') : '—',
              },
            ]}
          />
        </EdrDetailSectionCard>
      </>
    ),
    sidebar: (
      <EdrDetailEndpointSidebar
        endpoint={item.endpoint || '—'}
        hostName={item.hostName}
        organization={item.organization}
      />
    ),
  };
};
